import {Timestamp} from '@wandb/weave/components/Timestamp';
import {useNodeValue} from '@wandb/weave/react';
import React, {useMemo} from 'react';

import {useUpdateArtifactMutation} from '../../../generated/graphql';
import {useNavigateTo} from '../../../util/history';
import {artifactTab as artifactTabUrl, job as jobUrl} from '../../../util/urls';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {Aliases} from '../Aliases';
import {TargetBlank} from '../Link';
import {SelectJobVersion} from '../SelectJobVersion';
import {User} from '../User';
import {ArtifactFiles, ArtifactFilesInner} from './ArtifactFiles';
import {CopyableText} from './CopyableText';
import {LinkGit} from './LinkGit';
import {MarkdownEditor} from './MarkdownEditor';
import * as MS from './MetadataValue.styles';
import {PropertyTable} from './PropertyTable';
import * as S from './TabVersionMetadata.styles';
import {getJobDefinitionFileOp, getJobMetadataOp} from './utilsJobFile';
import {JobDetails, JobVersion} from './utilTypes';

const getSelectedVersion = (
  versions: JobVersion[],
  alias: string
): JobVersion => {
  for (const version of versions) {
    for (const versionAlias of version.aliases) {
      if (versionAlias.alias === alias) {
        return version;
      }
    }
  }
  throw new Error(`Could not find ${alias} in versions`);
};

type TabVersionMetadataProps = {
  entityName: string;
  projectName: string;
  job: JobDetails;
  jobVersion: string;
};

export const TabVersionMetadata = ({
  entityName,
  projectName,
  job,
  jobVersion,
}: TabVersionMetadataProps) => {
  const navigateTo = useNavigateTo();
  const onSelectJobAlias = (jobAlias: string) => {
    const urlParams = {
      entityName,
      projectName,
      jobId: job.id,
      jobVersion: jobAlias,
      jobTab: 'version_details',
    };
    const pathname = jobUrl(urlParams);
    navigateTo({pathname});
  };

  const [updateArtifact] = useUpdateArtifactMutation();
  const onUpdateDescription = (newDescription: string) => {
    updateArtifact({
      variables: {
        artifactID: selectedVersion.id,
        description: newDescription,
      },
    });
  };

  // TODO: Artifact URL is not in the design, it is here for dev debugging
  const artifactUrlParams = {
    entityName,
    projectName,
    artifactTypeName: 'job',
    artifactCollectionName: job.name,
    artifactCommitHash: jobVersion,
    tabName: 'overview',
  };
  const urlArtifact = artifactTabUrl(artifactUrlParams);

  const selectedVersion = getSelectedVersion(job.versions, jobVersion);
  const {aliases, createdByUser, createdAt, description} = selectedVersion;

  // Get and parse job definition
  const contents = useMemo(
    () =>
      getJobDefinitionFileOp({
        entityName,
        projectName,
        jobName: job.name,
        alias: jobVersion,
      }),
    [entityName, projectName, job.name, jobVersion]
  );
  const metadataContents = getJobMetadataOp({
    entityName,
    projectName,
    jobName: job.name,
    alias: jobVersion,
  });

  const {result: metadataResult, loading: metadataLoading} =
    useNodeValue(metadataContents);
  const {result, loading} = useNodeValue(contents);
  if (loading || !result || metadataLoading || !metadataResult) {
    return <Loader active name="tab-version-metadata-loader" />;
  }

  const value = JSON.parse(result);
  const inputValues =
    metadataResult.input_types != null
      ? metadataResult.input_types
      : value.input_types;

  const sourceArguments = value.source.args
    ? value.source.args.join(' ')
    : 'N/A';

  const versionPath = `${entityName}/${projectName}/${job.name}:${jobVersion}`;

  return (
    <>
      <S.SectionTitle>Job version</S.SectionTitle>
      <div style={{width: '200px'}}>
        <SelectJobVersion
          jobVersions={job.versions}
          selectedAlias={jobVersion}
          onSelectJobAlias={onSelectJobAlias}
        />
      </div>
      <S.SectionTitle>Version metadata</S.SectionTitle>
      <S.MetadataTable>
        <S.Tr>
          <S.Property>Version path</S.Property>
          <CopyableText
            text={versionPath}
            toastText="Successfully copied version path"
          />
        </S.Tr>
        <S.Tr>
          <S.Property>Description</S.Property>
          <MarkdownEditor
            content={description}
            onSave={onUpdateDescription}
            placeholder="What makes this job version special?"
          />
        </S.Tr>
        <S.Tr>
          <S.Property>Aliases</S.Property>
          <MS.MainPanel>
            <MS.ContentPanel>
              <Aliases aliases={aliases} />
            </MS.ContentPanel>
          </MS.MainPanel>
        </S.Tr>
        <S.Tr>
          <S.Property>Creator</S.Property>
          <MS.MainPanel>
            <MS.ContentPanel>
              {createdByUser && <User {...createdByUser} />}
            </MS.ContentPanel>
          </MS.MainPanel>
        </S.Tr>
        <S.Tr>
          <S.Property>Creation date</S.Property>
          <MS.MainPanel>
            <MS.ContentPanel>
              <Timestamp value={createdAt} />
            </MS.ContentPanel>
          </MS.MainPanel>
        </S.Tr>
        <S.Tr>
          <S.Property>Python version</S.Property>
          <MS.MainPanel>
            <MS.ContentPanel>{value.runtime} </MS.ContentPanel>
          </MS.MainPanel>
        </S.Tr>
        {value.source.entrypoint && (
          <S.Tr>
            <S.Property>Entry point</S.Property>
            <MS.MainPanel>
              <MS.ContentPanel>
                {value.source.entrypoint.join(' ')}
              </MS.ContentPanel>
            </MS.MainPanel>
          </S.Tr>
        )}
        <S.Tr>
          <S.Property>Arguments</S.Property>
          <MS.MainPanel>
            <MS.ContentPanel>{sourceArguments}</MS.ContentPanel>
          </MS.MainPanel>
        </S.Tr>
        <S.Tr>
          <S.Property>Source type</S.Property>
          <MS.MainPanel>
            <MS.ContentPanel>
              {value.source_type === 'repo' ? (
                <LinkGit git={value.source.git}>Git repo</LinkGit>
              ) : (
                value.source_type
              )}{' '}
              {value.source_type === 'image' && `(${value.source.image})`}
            </MS.ContentPanel>
          </MS.MainPanel>
        </S.Tr>
        <S.Tr>
          <S.Property>Artifact link</S.Property>
          <MS.MainPanel>
            <MS.ContentPanel>
              <TargetBlank href={urlArtifact}>View in Artifacts</TargetBlank>
            </MS.ContentPanel>
          </MS.MainPanel>
        </S.Tr>
      </S.MetadataTable>
      <PropertyTable label="Input types" values={inputValues} />
      <PropertyTable label="Output types" values={value.output_types} />
      {value.source.artifact && (
        <>
          <S.SectionTitle>Code</S.SectionTitle>
          <ArtifactFiles
            id={value.source.artifact}
            entityName={entityName}
            projectName={projectName}
            artifactType="code"
            jobName={job.name}
            alias={jobVersion}
          />
        </>
      )}
      <S.SectionTitle>Files</S.SectionTitle>
      <ArtifactFilesInner
        id={selectedVersion.id}
        entityName={entityName}
        projectName={projectName}
        artifactType="job"
        artifactVersionName={`${job.name}:${jobVersion}`}
      />
    </>
  );
};
